import React, { useMemo } from 'react';
import { BsAward, BsCalendar } from 'react-icons/bs';
import { IoIosGlobe } from 'react-icons/io';
import { Box, Flex, HStack, Icon, Link, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { analyticsIds } from '../../../../constants/analyticsIds';
import DataTypes from '../../../../constants/dataTypes';
import { Statuses } from '../../../../constants/statuses';
import colors from '../../../../styles/theme/foundation/colors';
import { get } from '../../../../utils/arrayUtil';
import { getDateString } from '../../../../utils/dateUtil';
import { getDisplayValueForType } from '../../../../utils/tableUtil';
import LoadingSpinner from '../../../Spinner';
import StatusBadge from '../../../StatusBadge';

import BooleanPolicyProperties from './BooleanPolicyProperties';
import NonbooleanPolicyProperties from './NonbooleanPolicyProperties';

const InsurancePolicyCard = ({ policy }) => {
  const properties = Object.values(policy.propertyValues);

  const styles = useMemo(() => {
    const expired = (policy.status === Statuses.Expired);

    return {
      color: (expired && !policy.isLoading) ? colors.error.primary['600'] : 'inherit',
      borderColor: (expired && !policy.isLoading) ? colors.error.primary['600'] : colors.greyScale['200'],
      borderWidth: (expired && !policy.isLoading) ? '2px' : '1px',
      boxShadow: (expired && !policy.isLoading) ? 'lg' : 'md'
    };
  }, [policy]);

  const dateString = useMemo(() => {
    const start = getDateString(policy.effectiveAt, 'MM/DD/YYYY');
    const end = getDateString(policy.expiresAt, 'MM/DD/YYYY');

    return `${start} - ${end}`;
  }, [policy]);

  const booleanProperties = properties
    .filter(p => p.insurancePolicyTypeProperty.dataType === DataTypes.Boolean)
    .map(prop => {
      if (parseInt(get(prop, 'value', 0), 10) > 0) {
        return (
          <BooleanPolicyProperties
            displayName={prop.insurancePolicyTypeProperty.displayName}
            expired={policy.status === Statuses.Expired}
            key={prop.id}
          />
        );
      }

      return null;
    });

  const nonBooleanProperties = properties
    .filter(p => p.insurancePolicyTypeProperty.dataType !== DataTypes.Boolean)
    .map(prop => {
      const propertyName = prop.insurancePolicyTypeProperty.displayName;
      const propertyValue = getDisplayValueForType(prop.value, prop.insurancePolicyTypeProperty.dataType) ?? '-';
      return (
        <NonbooleanPolicyProperties
          key={prop.id}
          propertyName={propertyName}
          propertyValue={propertyValue}
        />
      );
    });

  if (policy.isLoading) {
    return (
      <Box
        _hover={{ boxShadow: styles.boxShadow, cursor: 'pointer' }}
        alignItems="center"
        backgroundColor="white"
        borderColor={styles.borderColor}
        borderRadius="10px"
        borderWidth={styles.borderWidth}
        flexBasis="100%"
        h="150px"
        transition="0.25s"
      >
        <LoadingSpinner isInline minHeight="150px" />
      </Box>
    );
  }

  return (
    <Box
      _hover={{ boxShadow: styles.boxShadow, cursor: 'pointer' }}
      backgroundColor="white"
      borderColor={styles.borderColor}
      borderRadius="10px"
      borderWidth={styles.borderWidth}
      flexBasis="100%"
      mb="1rem"
      px="0.75rem"
      py="1rem"
      transition="0.25s"
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Text textStyle="bodyMd600">{policy.insurancePolicyType.displayName}</Text>
        <StatusBadge status={policy.status} />
      </Flex>

      <HStack>
        <Icon as={IoIosGlobe} color="greyScale.800" />
        <Link
          color="success.primary.600"
          data-analytics-id={analyticsIds.insuredProfilePage.policiesComponent.openInsuranceCompanyAMBestRating}
          href="http://www3.ambest.com/ratings/entities/SearchResults.aspx?AltSrc=9"
          maxW="container.sm"
          rel="noreferrer"
          target="_blank"
          textAlign="left"
        >
          {policy.insuranceCompany.companyName}
        </Link>
      </HStack>

      <HStack alignItems="flex-start" mt={2}>
        <Icon
          as={BsCalendar}
          boxSize="10px"
          color={styles.color}
          mt="5px"
        />
        <Text
          color={styles.color}
          textStyle="bodySm500"
        >
          {dateString}
        </Text>
      </HStack>

      <HStack alignItems="flex-start" mt={2}>
        <Icon
          as={BsAward}
          boxSize="10px"
          color={styles.color}
          mt="5px"
        />
        <Text
          color={styles.color}
          textStyle="bodySm500"
        >
          {policy.number}
        </Text>
      </HStack>

      <HStack alignItems="flex-start" mt={3} width="100%">
        <Flex direction="column" width="45%">
          <Text color={colors.greyScale['500']} textStyle="bodyXs500">Policy Properties: </Text>
          {booleanProperties}
        </Flex>
        <Flex direction="column" width="55%">
          {nonBooleanProperties}
        </Flex>
      </HStack>
    </Box>
  );
};

InsurancePolicyCard.propTypes = { policy: PropTypes.shape({ insuranceCompany: PropTypes.shape({ companyName: PropTypes.string }) }).isRequired };

export default InsurancePolicyCard;
